import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

import { AnalyticsFacade } from '@amfam/shared/analytics/lib/+state/analytics.facade';
import { OverviewAnalytics } from '@amfam/shared/models/lib/overview/overview-constants';
import { ContentDirective } from '@amfam/shared/utility/shared-services/lib/copy/content.directive';

import { LoadingSpinnerService } from '../services/loading-spinner.service';

@Component({
  selector: 'ds-loading-spinner',
  templateUrl: './loading-spinner.component.html',
  styleUrls: ['./loading-spinner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle, ContentDirective]
})
export class LoadingSpinnerComponent implements OnDestroy {
  private elRef = inject(ElementRef);
  private analyticsFacade = inject(AnalyticsFacade);
  private destroyRef = inject(DestroyRef);
  private spinner = inject(LoadingSpinnerService);

  readonly active = toSignal(this.spinner.status, { initialValue: false });
  readonly isNewLoader = toSignal(this.spinner.isNewLoaderSub, { initialValue: false });
  readonly isProgressBar = signal(false);
  readonly isRecalculate = toSignal(this.spinner.isRecalculateSub, { initialValue: false });
  readonly loadingMessage = toSignal(
    this.spinner.loadingMessageSub.pipe(
      filter(message => message !== null && message !== undefined)
    ),
    { initialValue: '' }
  );
  readonly secondaryMessage = toSignal(
    this.spinner.secondaryMessageSub.pipe(
      filter(message => message !== null && message !== undefined)
    ),
    { initialValue: '' }
  );
  readonly maxWidth = toSignal(this.spinner.maxWidthSub.pipe(filter(Boolean)), {
    initialValue: ''
  });
  readonly progress = signal(0);
  private blockActions = false;
  private interval = 0;
  private timeout = 0;
  private loading = true;

  constructor() {
    this.spinner.blockActionsStatus
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status: boolean) => {
        this.blockActions = status;

        if (this.elRef?.nativeElement) {
          if (this.blockActions) {
            this.elRef.nativeElement.classList.add('actionBlock');
          } else {
            this.elRef.nativeElement.classList.remove('actionBlock');
          }
        }
      });

    this.spinner.isProgressBarSub
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status: boolean) => {
        this.isProgressBar.set(status);
        clearInterval(this.interval);
        clearTimeout(this.timeout);
        this.timeout = window.setTimeout(() => {
          if (this.isProgressBar()) {
            const icons: NodeListOf<HTMLElement> = document.querySelectorAll('.afi-icon');
            this.progress.set(0);
            this.addAddition();
            let index = 0;

            this.interval = window.setInterval(() => {
              const currentIcon = icons[index];
              if (currentIcon) currentIcon.style.display = 'none';
              index++;
              if (index >= icons.length) {
                index = 0;
              }
              const nextIcon = icons[index];
              if (nextIcon) nextIcon.style.display = 'block';
            }, 1000);
          } else {
            this.isProgressBar.set(false);
          }
        }, 10);
      });
  }

  ngOnDestroy() {
    clearTimeout(this.timeout);
    clearInterval(this.interval);
  }

  public addAddition() {
    clearTimeout(this.timeout);
    this.timeout = window.setTimeout(() => {
      this.progress.update(progress => progress + this.randomIntFromInterval(progress));
      if (this.progress() === 100) {
        if (this.isRecalculate()) {
          this.analyticsFacade.trackPage(OverviewAnalytics.SpinnerRecalculateCompleteAnalytics);
        } else {
          this.analyticsFacade.trackPage(OverviewAnalytics.SpinnerCompleteAnalytics);
        }
      }
      if (this.progress() < 100) {
        if (this.loading) {
          this.addAddition();
        } else {
          this.progress.set(0);
        }
      }
    }, 1000);
  }

  private randomIntFromInterval(progressPer: number) {
    if (progressPer >= 0 && progressPer <= 80) {
      return 2;
    } else if (progressPer >= 81 && progressPer < 100) {
      return 1;
    } else if (progressPer >= 100) {
      return 0;
    }
    return 0;
  }
}
